import React, { useState, useRef, useEffect } from 'react';
import { DB, uploadImage } from '../data/database';
import { convertCurrency } from '../utils/currency';
import { useLanguage } from '../translations/LanguageContext';
import { CheckIcon, SearchIcon, CarIcon, EngineIcon, PartsIcon, PlusIcon } from './Icons';
import { Notifications } from '../data/notifications';
import centralUpdateStatus, { detectUnitType } from '../utils/updateUnitStatus';
import generateBrandedQR from '../utils/generateBrandedQR';
import { writeAuditLog } from '../utils/audit';

// ── Helpers ──────────────────────────────────────────────────
function getUnitName(u) { return u.make ? `${u.make} ${u.model || ''}`.trim() : u.engineName || u.partName || 'Unknown'; }
function getUnitIcon(u) { return u.make ? <CarIcon /> : u.engineName ? <EngineIcon /> : <PartsIcon />; }
function getDbKey(u) { return u._dbKey || (u.make ? 'vehicles' : u.engineName ? 'engines' : 'spareParts'); }
function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60000) return 'just now';
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ago`;
  if (ms < 86400000) return `${Math.floor(ms / 3600000)}h ago`;
  return `${Math.floor(ms / 86400000)}d ago`;
}

function toUsd(amount, currency) {
  if (!amount) return 0;
  return convertCurrency(amount, currency || 'USD', 'USD');
}

function formatCur(amount, cur) {
  const symbols = { AED: 'AED', USD: '$', JPY: '¥' };
  return `${symbols[cur] || cur} ${Number(amount).toLocaleString()}`;
}

// ── QR generation + print ────────────────────────────────────
async function ensureQR(unit) {
  if (unit.qr_code_url) return unit.qr_code_url;
  try {
    const qrContent = `${window.location.origin}/?u=${unit.sku || unit.id}`;
    const dataUrl = await generateBrandedQR(qrContent, { size: 512 });
    const url = await uploadImage(dataUrl);
    if (url) {
      const key = unit._dbKey || (unit.make ? 'vehicles' : unit.engineName ? 'engines' : 'spareParts');
      const list = DB.get(key) || [];
      DB.set(key, list.map(u => u.id === unit.id ? { ...u, qr_code_url: url } : u));
      return url;
    }
    return dataUrl;
  } catch { return ''; }
}

function printQRGrid(units, qrUrls, size) {
  const isSmall = size === 'small';
  const px = isSmall ? 113 : 151; // 30mm≈113px, 40mm≈151px at 96dpi
  const cols = isSmall ? 6 : 4;
  const gap = isSmall ? 4 : 8;

  const cells = units.map((u, i) => {
    const qr = qrUrls[i] || '';
    return `<div style="width:${px}px;text-align:center;page-break-inside:avoid">
      <img src="${qr}" style="width:${px}px;height:${px}px;image-rendering:pixelated" />
      ${!isSmall ? '<div style="width:8px;height:8px;border:1px solid #000;border-radius:50%;margin:4px auto 0"></div>' : ''}
      <div style="font-size:6px;color:#999;margin-top:2px">${u.sku || ''}</div>
    </div>`;
  }).join('');

  const html = `<!DOCTYPE html><html><head><style>
    @page{margin:10mm}body{margin:0;font-family:sans-serif}
    .grid{display:flex;flex-wrap:wrap;gap:${gap}px}
    @media print{.no-print{display:none}}
  </style></head><body>
    <div class="no-print" style="padding:10px;text-align:center">
      <button onclick="window.print()" style="padding:10px 24px;font-size:16px;cursor:pointer">🖨 Print</button>
      <span style="margin-left:12px;color:#666">${units.length} QR codes · ${isSmall ? '30×30mm labels' : '40×40mm tags with hole marker'}</span>
    </div>
    <div class="grid">${cells}</div>
  </body></html>`;

  const w = window.open('', '_blank');
  if (w) { w.document.write(html); w.document.close(); }
}

function printSingleQR(qrUrl, size) {
  const px = size === 'small' ? 113 : 151;
  const html = `<!DOCTYPE html><html><head><style>
    @page{size:${size === 'small' ? '30mm 30mm' : '40mm 44mm'};margin:0}
    body{margin:0;display:flex;align-items:center;justify-content:center;height:100vh}
    img{width:${px}px;height:${px}px;image-rendering:pixelated}
  </style></head><body>
    <div style="text-align:center">
      <img src="${qrUrl}" />
      ${size !== 'small' ? '<div style="width:6px;height:6px;border:1px solid #000;border-radius:50%;margin:3px auto 0"></div>' : ''}
    </div>
    <script>window.onload=()=>window.print()<\/script>
  </body></html>`;
  const w = window.open('', '_blank');
  if (w) { w.document.write(html); w.document.close(); }
}

// ── Compress + upload photo ──────────────────────────────────
async function compressAndUpload(file) {
  if (!file || !file.type.startsWith('image/')) return '';
  const compressed = await new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1200;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
          else { width = Math.round(width * MAX / height); height = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.75));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
  return await uploadImage(compressed);
}

// ── Phone formatter ──────────────────────────────────────────
function formatPhone(raw) {
  let p = raw.replace(/[^\d+]/g, '');
  if (p.startsWith('05') && !p.startsWith('+')) p = '+971' + p.substring(1);
  if (p.startsWith('5') && p.length >= 9 && !p.startsWith('+')) p = '+971' + p;
  return p;
}

// ── Find or create customer by phone ─────────────────────────
function findCustomerByPhone(phone) {
  const customers = DB.get('customers') || [];
  const clean = phone.replace(/[^\d+]/g, '');
  return customers.find(c => {
    const cp = (c.phone || '').replace(/[^\d+]/g, '');
    if (!cp || !clean) return false;
    return cp === clean || cp.endsWith(clean.slice(-9)) || clean.endsWith(cp.slice(-9));
  });
}

function createWalkInCustomer(phone, name) {
  const customers = DB.get('customers') || [];
  const newCust = {
    id: DB.genId('C'),
    name: name.trim(),
    phone: formatPhone(phone),
    country: 'UAE',
    currency: 'AED',
    is_walk_in: true,
    createdAt: new Date().toISOString().split('T')[0],
  };
  DB.set('customers', [...customers, newCust]);
  return newCust;
}

function getCustomerPurchaseCount(custId) {
  const all = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])];
  return all.filter(u => u.assignedTo === custId && ['sold', 'delivered'].includes(u.status)).length;
}

// ══════════════════════════════════════════════════════════════
// ADD BID MODAL
// ══════════════════════════════════════════════════════════════
function AddBidModal({ unit, user, onClose, onSaved }) {
  const phoneRef = useRef();
  const [phone, setPhone] = useState('');
  const [custName, setCustName] = useState('');
  const [foundCust, setFoundCust] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(() => localStorage.getItem('bigstar_yard_cur') || 'AED');
  const [notes, setNotes] = useState('');
  const [existingBid, setExistingBid] = useState(null);
  const [saved, setSaved] = useState(false);

  const bids = (DB.get('yardBids') || []).filter(b => b.unit_id === unit.id && b.status === 'active');
  const highest = bids.length > 0 ? bids.reduce((max, b) => (toUsd(b.amount, b.currency) > toUsd(max.amount, max.currency) ? b : max), bids[0]) : null;

  useEffect(() => { phoneRef.current?.focus(); }, []);

  // Auto-search customer as phone is typed
  useEffect(() => {
    const clean = phone.replace(/[^\d+]/g, '');
    if (clean.length >= 6) {
      const found = findCustomerByPhone(clean);
      if (found) {
        setFoundCust(found);
        setCustName(found.name);
        setIsNew(false);
        // Check for existing bid on this unit
        const eb = bids.find(b => b.customer_id === found.id);
        setExistingBid(eb || null);
      } else {
        setFoundCust(null);
        setIsNew(true);
        setExistingBid(null);
      }
    } else {
      setFoundCust(null);
      setIsNew(false);
      setExistingBid(null);
    }
  }, [phone]); // eslint-disable-line

  const handleSubmit = () => {
    const amt = Number(amount);
    if (!amt || (!foundCust && !custName.trim())) return;

    const customer = foundCust || createWalkInCustomer(phone, custName);
    const amtUsd = toUsd(amt, currency);

    localStorage.setItem('bigstar_yard_cur', currency);

    // Update existing bid or create new
    const allBids = DB.get('yardBids') || [];
    if (existingBid) {
      DB.setAsync('yardBids', allBids.map(b => b.id === existingBid.id ? {
        ...b, amount: amt, currency, amount_usd: amtUsd, notes: notes.trim(), updated_at: new Date().toISOString(),
      } : b));
    } else {
      const bid = {
        id: DB.genId('BID'),
        unit_id: unit.id,
        unit_type: unit.make ? 'vehicle' : unit.engineName ? 'engine' : 'spare_part',
        unit_sku: unit.sku,
        unit_description: getUnitName(unit),
        customer_id: customer.id,
        customer_name: customer.name,
        customer_phone: formatPhone(phone),
        is_repeat_buyer: getCustomerPurchaseCount(customer.id) > 0,
        amount: amt,
        currency,
        amount_usd: amtUsd,
        status: 'active',
        yard_location: unit.yard_location || 'sharjah_yard',
        notes: notes.trim(),
        entered_by: user.id,
        entered_by_name: user.name,
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        created_at: new Date().toISOString(),
      };
      DB.setAsync('yardBids', [...allBids, bid]);
    }

    setSaved(true);
    setTimeout(() => { onSaved(); onClose(); }, 800);
  };

  if (saved) {
    return (
      <div className="modal-body" style={{ padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
        <div style={{ fontSize: 14, fontWeight: 700 }}>Bid recorded: {formatCur(amount, currency)}</div>
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>from {custName || foundCust?.name}</div>
      </div>
    );
  }

  const amtUsd = Number(amount) ? toUsd(Number(amount), currency) : 0;
  const purchases = foundCust ? getCustomerPurchaseCount(foundCust.id) : 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Add Price Bid</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ padding: 16 }}>
          {/* Unit info */}
          <div style={{ display: 'flex', gap: 10, padding: 10, background: 'var(--bg3)', borderRadius: 8, marginBottom: 14, alignItems: 'center' }}>
            <div style={{ width: 48, height: 36, borderRadius: 6, overflow: 'hidden', background: 'var(--bg)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {unit.images?.length ? <img src={unit.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: 'var(--text3)' }}>{getUnitIcon(unit)}</span>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{getUnitName(unit)}</div>
              <div style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'monospace' }}>{unit.sku}</div>
            </div>
          </div>

          {highest && (
            <div style={{ fontSize: 11, color: 'var(--yellow)', marginBottom: 12 }}>
              Current highest: {formatCur(highest.amount, highest.currency)} ({highest.customer_name})
            </div>
          )}

          {/* Customer — phone lookup */}
          <div style={{ marginBottom: 12 }}>
            <label className="form-label">Phone</label>
            <input ref={phoneRef} className="form-input" value={phone} onChange={e => setPhone(e.target.value)}
              placeholder="+971 55..." style={{ fontSize: 15, fontFamily: 'monospace' }}
              onKeyDown={e => { if (e.key === 'Tab' && foundCust) e.target.blur(); }} />

            {foundCust && (
              <div style={{ marginTop: 6, padding: '8px 10px', background: 'rgba(63,185,80,0.08)', borderRadius: 6, border: '1px solid rgba(63,185,80,0.2)', fontSize: 12 }}>
                <span style={{ color: 'var(--green)', fontWeight: 700 }}>✅ {foundCust.name}</span>
                {foundCust.company && <span style={{ color: 'var(--text3)', marginLeft: 6 }}>— {foundCust.company}</span>}
                {purchases > 0 && <span style={{ color: 'var(--blue)', marginLeft: 6 }}>Repeat buyer ({purchases} purchases)</span>}
              </div>
            )}
            {isNew && phone.replace(/[^\d+]/g, '').length >= 6 && (
              <div style={{ marginTop: 6 }}>
                <div style={{ fontSize: 11, color: 'var(--yellow)', marginBottom: 4 }}>New customer — enter name:</div>
                <input className="form-input" value={custName} onChange={e => setCustName(e.target.value)}
                  placeholder="Customer name" style={{ fontSize: 13 }} />
              </div>
            )}
            {existingBid && (
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--yellow)' }}>
                {foundCust?.name} already has a bid of {formatCur(existingBid.amount, existingBid.currency)}. New amount will update it.
              </div>
            )}
          </div>

          {/* Price */}
          <div style={{ marginBottom: 12 }}>
            <label className="form-label">Price</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="number" className="form-input" value={amount} onChange={e => setAmount(e.target.value)}
                placeholder="0" style={{ fontSize: 18, fontWeight: 700, flex: 1 }}
                onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }} />
              <select className="form-select" value={currency} onChange={e => setCurrency(e.target.value)} style={{ width: 90 }}>
                <option value="AED">AED</option>
                <option value="USD">USD</option>
                <option value="JPY">JPY</option>
              </select>
            </div>
            {amtUsd > 0 && currency !== 'USD' && (
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>= ${amtUsd.toLocaleString()} USD</div>
            )}
          </div>

          {/* Notes */}
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label className="form-label">Notes (optional)</label>
            <input className="form-input" value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Cash ready, wants delivery" style={{ fontSize: 12 }} />
          </div>

          <button className="btn btn-success" style={{ width: '100%', padding: '14px', fontSize: 15 }}
            disabled={!Number(amount) || (!foundCust && !custName.trim()) || phone.replace(/[^\d+]/g, '').length < 6}
            onClick={handleSubmit}>
            <CheckIcon /> {existingBid ? 'Update Bid' : 'Submit Bid'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// VIEW ALL BIDS MODAL
// ══════════════════════════════════════════════════════════════
function ViewBidsModal({ unit, user, onClose, onSaved, onGoToCustomer }) {
  const [showAddBid, setShowAddBid] = useState(false);
  const [sellConfirm, setSellConfirm] = useState(null);
  const [showFinalBids, setShowFinalBids] = useState(false);
  const [showCancelAll, setShowCancelAll] = useState(false);
  const [, forceUpdate] = useState();

  const isAdmin = user.role === 'head_admin' || user.role === 'admin';
  const isYardSales = user.role === 'yard_sales';
  const canSell = isAdmin || isYardSales;
  const allBids = DB.get('yardBids') || [];
  const unitBids = allBids.filter(b => b.unit_id === unit.id);
  const activeBids = unitBids.filter(b => b.status === 'active').sort((a, b) => toUsd(b.amount, b.currency) - toUsd(a.amount, a.currency));
  const inactiveBids = unitBids.filter(b => b.status !== 'active');
  const pendingSale = unitBids.find(b => b.selected_for_sale && b.status === 'active');
  const otherActiveBids = (bid) => activeBids.filter(b => b.id !== bid.id);

  // ── BUTTON 1: Sell to this Bidder ──
  // WAVE 1 FIX: Full try/catch, verified awaits, honest audit logging
  // KNOWN REMAINING BUGS (DO NOT FIX IN WAVE 1):
  //   - No sold_price field written to unit record
  //   - No yardBidId field in invoiceToRow mapper (lost on sync)
  //   - No dedicated accountant UI for yard sale invoices
  //   - WhatsApp opens multiple tabs (browser may block popups)
  const handleSell = async (bid) => {
    const losingBids = otherActiveBids(bid);

    if (isAdmin) {
      try {
        // STEP 1: Update bid statuses — winning bid to 'won', others to 'lost'
        const updBids = allBids.map(b => {
          if (b.id === bid.id) return { ...b, status: 'won', selected_for_sale: true, sale_approved_by: user.id, sale_approved_at: new Date().toISOString() };
          if (b.unit_id === unit.id && b.status === 'active') return { ...b, status: 'lost' };
          return b;
        });
        const bidsResult = await DB.setAsync('yardBids', updBids);
        if (bidsResult && !bidsResult.success) {
          const err = new Error('Failed to persist bid status update: ' + (bidsResult.error || 'unknown'));
          err.step = 'yardBids_update';
          throw err;
        }

        // STEP 2: Update unit status via central handler
        const statusResult = centralUpdateStatus(unit.id, detectUnitType(unit), 'sold_awaiting_payment', { assignedTo: bid.customer_id, updatedBy: user.name });
        if (statusResult?.error) {
          const err = new Error('Unit status update failed: ' + statusResult.error);
          err.step = 'unit_status_update';
          throw err;
        }

        // STEP 3: Create yard sale invoice
        // Use async invoice number — queries Supabase for actual max, avoids phantom conflicts
        const invNum = await DB.nextInvoiceNumberAsync();
        const bidAmount = Number(bid.amount) || 0;
        const invoice = {
          id: DB.genId('INV'), invoiceNumber: invNum,
          customerId: bid.customer_id, customerName: bid.customer_name,
          invoiceType: 'sale', invoiceCategory: 'yard_sale',
          lineItems: [{ unit_id: unit.id, unit_sku: unit.sku, description: getUnitName(unit), fob_price: bidAmount, total: bidAmount }],
          subtotal: bidAmount, tax: 0, total: bidAmount, grandTotal: bidAmount,
          currency: bid.currency, status: 'draft',
          issuedDate: new Date().toISOString().split('T')[0],
          amountPaid: 0, balanceDue: bidAmount,
          paymentMilestones: [],
          createdBy: user.id, createdAt: new Date().toISOString().split('T')[0],
          yardBidId: bid.id,
        };
        // Push ONLY the new invoice to Supabase (avoids pushing phantom invoices from localStorage)
        const invResult = await DB.setAsync('invoices', [invoice]);
        if (invResult && !invResult.success) {
          const err = new Error('Failed to persist invoice: ' + (invResult.error || 'unknown'));
          err.step = 'invoice_create';
          throw err;
        }
        // After successful cloud push, add to localStorage for local UI display
        const localInvoices = DB.get('invoices') || [];
        if (!localInvoices.find(i => i.id === invoice.id)) {
          localInvoices.push(invoice);
          localStorage.setItem('bigstar_invoices', JSON.stringify(localInvoices));
        }

        // STEP 4: Remove unit from other customers' stock
        const stock = DB.get('customerStock') || [];
        const updatedStock = stock.filter(s => s.unitId !== unit.id || s.customerId === bid.customer_id);
        if (updatedStock.length !== stock.length) {
          const stockResult = await DB.setAsync('customerStock', updatedStock);
          if (stockResult && !stockResult.success) {
            const err = new Error('Failed to update customer stock: ' + (stockResult.error || 'unknown'));
            err.step = 'customer_stock_cleanup';
            throw err;
          }
        }

        // ALL PERSISTENCE SUCCEEDED — now fire non-critical side effects

        // Notification for accountant
        Notifications.add('invoice_created', 'Yard Sale Invoice Draft', `Invoice #${invNum} created for ${bid.customer_name} — ${formatCur(bid.amount, bid.currency)} for ${getUnitName(unit)}. Review and send from Invoices page.`, {});

        // WhatsApp to WINNER
        const winPh = (bid.customer_phone || '').replace(/[^\d]/g, '');
        const winMsg = encodeURIComponent(`Hello ${bid.customer_name}, your bid of ${bid.currency} ${bid.amount?.toLocaleString()} for ${getUnitName(unit)} has been accepted ✅ Please visit BIG STAR Sharjah to make payment and collect. — BIG STAR`);
        window.open(`https://wa.me/${winPh}?text=${winMsg}`, '_blank');

        // WhatsApp to each LOSER
        setTimeout(() => {
          losingBids.forEach(lb => {
            const ph = (lb.customer_phone || '').replace(/[^\d]/g, '');
            const msg = encodeURIComponent(`Hello ${lb.customer_name}, unfortunately the ${getUnitName(unit)} you bid on has been sold to another buyer. Thank you for your interest. Please contact us for similar units. — BIG STAR`);
            window.open(`https://wa.me/${ph}?text=${msg}`, '_blank');
          });
        }, 1000);

        // SUCCESS audit log — ONLY fires if all persistence succeeded
        await writeAuditLog({
          action: 'yard_sale.bid_won',
          entityType: 'yard_bid',
          entityId: bid.id,
          entityLabel: `${bid.customer_name} - ${getUnitName(unit)}`,
          details: {
            unit_id: unit.id, unit_sku: unit.sku,
            winning_bid_id: bid.id,
            winning_amount: bid.amount,
            winning_currency: bid.currency,
            losing_bid_ids: losingBids.map(b => b.id),
            notifications_sent: [winPh, ...losingBids.map(b => (b.customer_phone || '').replace(/[^\d]/g, ''))],
            invoice_number: invNum,
          },
        });

        setSellConfirm(null);
        onSaved();
        forceUpdate({});

      } catch (err) {
        // FAILURE — show user-facing error
        console.error('[handleSell FAILED]', err);
        alert('Sale could not be completed.\n\nError: ' + (err?.message || 'Unknown error') + '\n\nStep: ' + (err?.step || 'unknown') + '\n\nNo data was saved to cloud. Please try again or contact admin.');

        // FAILURE audit log — records what went wrong
        await writeAuditLog({
          action: 'yard_sale.sell_failed',
          entityType: 'yard_bid',
          entityId: bid?.id || 'unknown',
          entityLabel: `${bid?.customer_name || 'unknown'} - ${getUnitName(unit)}`,
          details: {
            error_message: err?.message,
            error_step: err?.step || 'unknown',
            attempted_amount: bid?.amount,
            attempted_currency: bid?.currency,
            unit_id: unit?.id,
            unit_sku: unit?.sku,
          },
        });

        setSellConfirm(null);
      }

    } else {
      // Non-admin requests approval
      try {
        const reqResult = await DB.setAsync('yardBids', allBids.map(b => b.id === bid.id ? { ...b, selected_for_sale: true } : b));
        if (reqResult && !reqResult.success) {
          alert('Failed to submit approval request: ' + (reqResult.error || 'Unknown error'));
          return;
        }

        Notifications.add('general', 'Sale Approval Needed', `${user.name} requests approval: ${getUnitName(unit)} to ${bid.customer_name} at ${formatCur(bid.amount, bid.currency)}`, {});

        await writeAuditLog({
          action: 'yard_sale.approval_requested',
          entityType: 'yard_bid',
          entityId: bid.id,
          entityLabel: `${bid.customer_name} - ${getUnitName(unit)}`,
          details: { unit_id: unit.id, amount: bid.amount, currency: bid.currency, requested_by: user.name },
        });

        setSellConfirm(null);
        onSaved();
        forceUpdate({});

      } catch (err) {
        console.error('[handleSell approval request FAILED]', err);
        alert('Approval request failed: ' + (err?.message || 'Unknown error'));
        setSellConfirm(null);
      }
    }
  };

  // ── BUTTON 2: Ask for Final Bids ──
  const handleFinalBids = async (deadline, optionalMsg) => {
    // Update bids with deadline
    const updBids = allBids.map(b => {
      if (b.unit_id === unit.id && b.status === 'active') {
        return { ...b, final_bid_deadline: deadline };
      }
      return b;
    });
    DB.setAsync('yardBids', updBids);

    // Set unit-level flag
    const dbKey = unit.make ? 'vehicles' : unit.engineName ? 'engines' : 'spareParts';
    const list = DB.get(dbKey) || [];
    DB.set(dbKey, list.map(u => u.id === unit.id ? { ...u, final_bids_requested: true, final_bids_deadline: deadline } : u));

    // WhatsApp each active bidder
    activeBids.forEach(bid => {
      const ph = (bid.customer_phone || '').replace(/[^\d]/g, '');
      const deadlineStr = new Date(deadline).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
      const msg = encodeURIComponent(`Hello ${bid.customer_name}, final bids are being collected for ${getUnitName(unit)}. If you wish to raise your offer of ${bid.currency} ${bid.amount?.toLocaleString()}, please submit before ${deadlineStr}.${optionalMsg ? ' ' + optionalMsg : ''} — BIG STAR`);
      window.open(`https://wa.me/${ph}?text=${msg}`, '_blank');
    });

    Notifications.add('general', 'Final Bids Requested', `Final bids requested for ${getUnitName(unit)}. Deadline: ${new Date(deadline).toLocaleString()}. ${activeBids.length} bidders notified.`, {});

    writeAuditLog({
      action: 'yard_sale.final_bids_requested',
      entityType: 'unit',
      entityId: unit.id,
      entityLabel: getUnitName(unit),
      details: {
        deadline,
        bidders_notified: activeBids.map(b => (b.customer_phone || '').replace(/[^\d]/g, '')),
        optional_message: optionalMsg || null,
        bid_count: activeBids.length,
      },
    });

    setShowFinalBids(false);
    onSaved();
    forceUpdate({});
  };

  // ── BUTTON 3: Cancel All Bids ──
  const handleCancelAll = async (reason) => {
    const cancelledIds = activeBids.map(b => b.id);
    const updBids = allBids.map(b => {
      if (b.unit_id === unit.id && b.status === 'active') {
        return { ...b, status: 'cancelled', cancellation_reason: reason };
      }
      return b;
    });
    DB.setAsync('yardBids', updBids);

    // WhatsApp each bidder
    activeBids.forEach(bid => {
      const ph = (bid.customer_phone || '').replace(/[^\d]/g, '');
      const msg = encodeURIComponent(`Hello ${bid.customer_name}, the ${getUnitName(unit)} you bid on is no longer available for yard sale. Thank you for your interest. Please contact us for similar units. — BIG STAR`);
      window.open(`https://wa.me/${ph}?text=${msg}`, '_blank');
    });

    Notifications.add('general', 'Bids Cancelled', `All bids cancelled for ${getUnitName(unit)}. Reason: ${reason}. ${activeBids.length} bidders notified.`, {});

    writeAuditLog({
      action: 'yard_sale.bids_cancelled',
      entityType: 'unit',
      entityId: unit.id,
      entityLabel: getUnitName(unit),
      details: {
        reason,
        cancelled_bid_ids: cancelledIds,
        bidders_notified: activeBids.map(b => (b.customer_phone || '').replace(/[^\d]/g, '')),
        bid_count: activeBids.length,
      },
    });

    setShowCancelAll(false);
    onSaved();
    forceUpdate({});
  };

  if (showAddBid) {
    return <AddBidModal unit={unit} user={user} onClose={() => setShowAddBid(false)} onSaved={() => { setShowAddBid(false); onSaved(); forceUpdate({}); }} />;
  }

  // ── SELL CONFIRMATION MODAL ──
  if (sellConfirm) {
    const losers = otherActiveBids(sellConfirm);
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">🏆 Sell to {sellConfirm.customer_name}</h3>
            <button className="modal-close" onClick={() => setSellConfirm(null)}>×</button>
          </div>
          <div className="modal-body" style={{ padding: 20 }}>
            <div style={{ fontSize: 13, marginBottom: 4 }}>Unit: <strong>{getUnitName(unit)}</strong></div>
            <div style={{ fontSize: 13, marginBottom: 4 }}>Selling to: <strong>{sellConfirm.customer_name}</strong> ({sellConfirm.customer_phone})</div>
            <div style={{ fontSize: 13, marginBottom: 4 }}>Bid amount: <strong>{formatCur(sellConfirm.amount, sellConfirm.currency)}</strong></div>
            {sellConfirm.currency !== 'USD' && <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>USD equivalent: ${(sellConfirm.amount_usd || 0).toLocaleString()}</div>}

            <div style={{ padding: '10px 12px', background: 'var(--bg3)', borderRadius: 8, marginBottom: 16, fontSize: 12, lineHeight: 1.8 }}>
              This will:<br/>
              ✓ Mark this bid as the winning bid<br/>
              ✓ Notify <strong>{sellConfirm.customer_name}</strong> to come and pay (WhatsApp)<br/>
              {losers.length > 0 && <>✓ Notify the other <strong>{losers.length}</strong> bidder{losers.length !== 1 ? 's' : ''} that the unit was sold<br/></>}
              ✓ Lock the unit (no more bids accepted)<br/>
              ✓ Generate a yard sale invoice
            </div>

            {!canSell && (
              <div style={{ fontSize: 11, color: 'var(--yellow)', marginBottom: 12 }}>You do not have permission to sell.</div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setSellConfirm(null)}>Cancel</button>
              <button className="btn btn-success" style={{ flex: 1 }} disabled={!canSell} onClick={() => handleSell(sellConfirm)}>
                <CheckIcon /> {isAdmin ? 'Confirm Sell' : 'Request Approval'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── FINAL BIDS MODAL ──
  if (showFinalBids) {
    return <FinalBidsModal unit={unit} activeBids={activeBids} onClose={() => setShowFinalBids(false)} onSubmit={handleFinalBids} />;
  }

  // ── CANCEL ALL MODAL ──
  if (showCancelAll) {
    return <CancelAllBidsModal unit={unit} activeBids={activeBids} onClose={() => setShowCancelAll(false)} onConfirm={handleCancelAll} />;
  }

  // ── MAIN BID LIST ──
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Bids — {getUnitName(unit)}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ padding: 0, maxHeight: '70vh', overflowY: 'auto' }}>
          {pendingSale && (
            <div style={{ padding: '10px 16px', background: 'rgba(210,153,34,0.08)', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--yellow)' }}>
              ⏳ Sale pending approval — {formatCur(pendingSale.amount, pendingSale.currency)} to {pendingSale.customer_name}
            </div>
          )}

          {/* Active bids */}
          {activeBids.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No active bids</div>
          ) : (
            <div style={{ padding: '8px 16px' }}>
              {activeBids.map((bid, i) => {
                const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`;
                const isSelected = bid.selected_for_sale;
                return (
                  <div key={bid.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0',
                    borderBottom: i < activeBids.length - 1 ? '1px solid var(--border)' : 'none',
                    opacity: isSelected ? 0.6 : 1,
                  }}>
                    <span style={{ fontSize: 16, width: 24, textAlign: 'center', flexShrink: 0 }}>{medal}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>
                        <span onClick={(e) => { e.stopPropagation(); if (onGoToCustomer && bid.customer_id) onGoToCustomer(bid.customer_id); }} style={{ cursor: bid.customer_id ? 'pointer' : 'default', textDecoration: bid.customer_id ? 'underline' : 'none', textDecorationStyle: 'dotted' }}>
                          {bid.customer_name}
                        </span>
                        {bid.is_repeat_buyer && <span style={{ color: 'var(--green)', marginLeft: 4, fontSize: 10 }}>✅</span>}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                        <a href={`https://wa.me/${(bid.customer_phone || '').replace(/[^\d]/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--cyan)', textDecoration: 'none' }}>
                          {bid.customer_phone}
                        </a>
                        {' · '}{timeAgo(bid.created_at)} · by {bid.entered_by_name}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 800 }}>{formatCur(bid.amount, bid.currency)}</div>
                      {bid.currency !== 'USD' && <div style={{ fontSize: 9, color: 'var(--text3)' }}>${(bid.amount_usd || 0).toLocaleString()}</div>}
                    </div>
                    <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: 'rgba(63,185,80,0.12)', color: 'var(--green)' }}>active</span>
                    {!pendingSale && canSell && (
                      <button className="btn btn-success btn-sm" style={{ flexShrink: 0, fontSize: 10 }}
                        onClick={() => setSellConfirm(bid)}>🏆 Sell</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Stats */}
          {activeBids.length > 0 && (
            <div style={{ padding: '8px 16px', background: 'var(--bg3)', borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--text3)', display: 'flex', gap: 12 }}>
              <span>Highest: {formatCur(activeBids[0].amount, activeBids[0].currency)}</span>
              <span>Avg: {formatCur(Math.round(activeBids.reduce((s, b) => s + b.amount, 0) / activeBids.length), activeBids[0].currency)}</span>
              <span>{activeBids.length} bid{activeBids.length !== 1 ? 's' : ''}</span>
            </div>
          )}

          {/* Inactive bids (won/lost/cancelled/expired) — historical record */}
          {inactiveBids.length > 0 && (
            <div style={{ padding: '8px 16px', borderTop: '1px solid var(--border)' }}>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 6 }}>Past bids ({inactiveBids.length}):</div>
              {inactiveBids.map(bid => {
                const stColor = bid.status === 'won' ? 'var(--green)' : bid.status === 'lost' ? 'var(--red)' : 'var(--text3)';
                return (
                  <div key={bid.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, padding: '3px 0', opacity: 0.5 }}>
                    <span style={{ flex: 1 }}>{bid.customer_name} — {formatCur(bid.amount, bid.currency)}</span>
                    <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 600, color: stColor, background: `${stColor}15` }}>{bid.status}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── ACTION BUTTONS ── */}
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAddBid(true)}><PlusIcon /> Add New Bid</button>

            {/* Button 2: Ask for Final Bids — admin only */}
            {isAdmin && activeBids.length > 0 && (
              <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => setShowFinalBids(true)}>
                📢 Ask for Final Bids
              </button>
            )}

            <div style={{ flex: 1 }} />

            {/* Button 3: Cancel All Bids — admin only, destructive */}
            {isAdmin && activeBids.length > 0 && (
              <button className="btn btn-secondary btn-sm" style={{ fontSize: 11, color: 'var(--red)', borderColor: 'var(--red)' }} onClick={() => setShowCancelAll(true)}>
                ❌ Cancel All Bids
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── FINAL BIDS MODAL ──
function FinalBidsModal({ unit, activeBids, onClose, onSubmit }) {
  const [deadline, setDeadline] = useState('3h');
  const [customDeadline, setCustomDeadline] = useState('');
  const [optionalMsg, setOptionalMsg] = useState('');

  const getDeadlineISO = () => {
    const now = Date.now();
    if (deadline === '1h') return new Date(now + 3600000).toISOString();
    if (deadline === '3h') return new Date(now + 3 * 3600000).toISOString();
    if (deadline === 'eod') {
      const eod = new Date(); eod.setHours(23, 59, 59, 0);
      return eod.toISOString();
    }
    if (deadline === 'custom' && customDeadline) return new Date(customDeadline).toISOString();
    return new Date(now + 3 * 3600000).toISOString();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">📢 Ask for Final Bids</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ padding: 20 }}>
          <div style={{ fontSize: 13, marginBottom: 12 }}>
            This will notify all <strong>{activeBids.length}</strong> active bidder{activeBids.length !== 1 ? 's' : ''} on <strong>{getUnitName(unit)}</strong> that the unit is closing soon and ask them to submit their final offer.
          </div>

          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Deadline:</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
            {[
              { id: '1h', label: '1 hour from now' },
              { id: '3h', label: '3 hours from now' },
              { id: 'eod', label: 'End of today' },
              { id: 'custom', label: 'Custom' },
            ].map(opt => (
              <label key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                <input type="radio" name="deadline" checked={deadline === opt.id} onChange={() => setDeadline(opt.id)} style={{ accentColor: 'var(--blue)' }} />
                {opt.label}
              </label>
            ))}
            {deadline === 'custom' && (
              <input type="datetime-local" className="form-input" style={{ fontSize: 12, marginLeft: 24 }} value={customDeadline} onChange={e => setCustomDeadline(e.target.value)} />
            )}
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Optional message:</div>
            <textarea className="form-input" rows={2} placeholder="Add a note to the default message (optional)..." value={optionalMsg} onChange={e => setOptionalMsg(e.target.value)} style={{ resize: 'vertical', fontSize: 12 }} />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => onSubmit(getDeadlineISO(), optionalMsg.trim())}>
              Send Final Bid Request
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── CANCEL ALL BIDS MODAL ──
function CancelAllBidsModal({ unit, activeBids, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');

  const reasons = [
    'Sent to overseas customer',
    'Going into cut plan',
    'Damaged / not for sale',
    'Pricing dispute',
  ];

  const finalReason = reason === 'other' ? customReason.trim() : reason;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">⚠️ Cancel All Bids</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ padding: 20 }}>
          <div style={{ padding: '10px 12px', background: 'rgba(248,81,73,0.08)', borderRadius: 8, marginBottom: 14, fontSize: 12, color: 'var(--red)', border: '1px solid rgba(248,81,73,0.2)' }}>
            This unit will be removed from yard sale. All <strong>{activeBids.length}</strong> active bidder{activeBids.length !== 1 ? 's' : ''} will be notified that <strong>{getUnitName(unit)}</strong> is no longer available.
          </div>

          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Reason for cancellation:</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
            {reasons.map(r => (
              <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                <input type="radio" name="cancel-reason" checked={reason === r} onChange={() => setReason(r)} style={{ accentColor: 'var(--red)' }} />
                {r}
              </label>
            ))}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
              <input type="radio" name="cancel-reason" checked={reason === 'other'} onChange={() => setReason('other')} style={{ accentColor: 'var(--red)' }} />
              Other:
            </label>
            {reason === 'other' && (
              <input className="form-input" style={{ fontSize: 12, marginLeft: 24 }} placeholder="Enter reason..." value={customReason} onChange={e => setCustomReason(e.target.value)} />
            )}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" style={{ flex: 1, background: 'var(--red)' }} disabled={!finalReason} onClick={() => onConfirm(finalReason)}>
              Confirm Cancel All Bids
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN YARD PRICING PAGE
// ══════════════════════════════════════════════════════════════
function YardPricing({ user, setPage }) {
  const { t } = useLanguage();
  const [containerFilter, setContainerFilter] = useState('all_sharjah');
  const [sortBy, setSortBy] = useState('most_bids');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [addBidUnit, setAddBidUnit] = useState(null);
  const [viewBidsUnit, setViewBidsUnit] = useState(null);
  const [qrGenerating, setQrGenerating] = useState('');
  const [condNoteUnit, setCondNoteUnit] = useState(null);
  const [holdReqUnit, setHoldReqUnit] = useState(null);
  const [printQrUnit, setPrintQrUnit] = useState(null);
  const [, forceUpdate] = useState(0);

  const isAdmin = user.role === 'head_admin' || user.role === 'admin';

  // Auto-refresh every 10 seconds
  useEffect(() => {
    const iv = setInterval(() => forceUpdate(v => v + 1), 10000);
    return () => clearInterval(iv);
  }, []);

  const allVehicles = DB.get('vehicles') || [];
  const allEngines = DB.get('engines') || [];
  const allParts = DB.get('spareParts') || [];
  const allUnits = [
    ...allVehicles.map(u => ({ ...u, _dbKey: 'vehicles', _type: 'vehicle' })),
    ...allEngines.map(u => ({ ...u, _dbKey: 'engines', _type: 'engine' })),
    ...allParts.map(u => ({ ...u, _dbKey: 'spareParts', _type: 'part' })),
  ];
  const containers = DB.get('containers') || [];
  const allBids = DB.get('yardBids') || [];

  // Sharjah units only
  const sharjahUnits = allUnits.filter(u => (u.yard_location || '') === 'sharjah_yard');

  // Filter by container
  let displayUnits;
  if (containerFilter === 'all_sharjah') {
    displayUnits = sharjahUnits;
  } else if (containerFilter === 'unlinked') {
    const linkedIds = new Set(containers.flatMap(c => c.units || []));
    displayUnits = sharjahUnits.filter(u => !linkedIds.has(u.id));
  } else {
    const cont = containers.find(c => c.id === containerFilter);
    displayUnits = cont ? sharjahUnits.filter(u => (cont.units || []).includes(u.id)) : sharjahUnits;
  }

  // Helpers
  const getBids = (unitId) => allBids.filter(b => b.unit_id === unitId && b.status === 'active');
  const getHighest = (unitId) => {
    const bids = getBids(unitId);
    return bids.length > 0 ? bids.reduce((max, b) => toUsd(b.amount, b.currency) > toUsd(max.amount, max.currency) ? b : max, bids[0]) : null;
  };
  const isSold = (u) => ['sold', 'sold_awaiting_payment', 'delivered', 'dismantled'].includes(u.status);
  const isNewArrival = (u) => {
    const at = u.arrived_at_yard || u.createdAt;
    return at && (Date.now() - new Date(at).getTime()) < 48 * 3600000;
  };
  const isOnHold = (u) => u.hold_for_customer_id && u.hold_until && new Date(u.hold_until) > new Date();
  const daysInYard = (u) => {
    const at = u.arrived_at_yard || u.createdAt;
    return at ? Math.floor((Date.now() - new Date(at).getTime()) / 86400000) : 0;
  };

  // Search
  if (search) {
    const q = search.toLowerCase();
    displayUnits = displayUnits.filter(u => [u.sku, u.make, u.model, u.chassis, u.engineName, u.partName, getUnitName(u)].filter(Boolean).join(' ').toLowerCase().includes(q));
  }

  // Status filter
  if (statusFilter === 'new') displayUnits = displayUnits.filter(isNewArrival);
  else if (statusFilter === 'has_bids') displayUnits = displayUnits.filter(u => getBids(u.id).length > 0);
  else if (statusFilter === 'no_bids') displayUnits = displayUnits.filter(u => getBids(u.id).length === 0 && !isSold(u));
  else if (statusFilter === 'on_hold') displayUnits = displayUnits.filter(isOnHold);

  // Sort
  displayUnits = [...displayUnits].sort((a, b) => {
    if (sortBy === 'most_bids') return getBids(b.id).length - getBids(a.id).length;
    if (sortBy === 'highest') return (getHighest(b.id)?.amount_usd || 0) - (getHighest(a.id)?.amount_usd || 0);
    if (sortBy === 'no_bids') return getBids(a.id).length - getBids(b.id).length;
    if (sortBy === 'days_in_yard') return daysInYard(b) - daysInYard(a);
    return 0;
  });

  // Summary stats
  const withBids = displayUnits.filter(u => getBids(u.id).length > 0 && !isSold(u)).length;
  const noBids = displayUnits.filter(u => getBids(u.id).length === 0 && !isSold(u)).length;
  const topTotal = displayUnits.reduce((s, u) => {
    const h = getHighest(u.id);
    return s + (h ? (h.amount_usd || 0) : 0);
  }, 0);

  // Sharjah containers
  const sharjahContainers = containers.filter(c => {
    const cUnits = (c.units || []).map(uid => allUnits.find(u => u.id === uid)).filter(Boolean);
    return cUnits.some(u => (u.yard_location || '') === 'sharjah_yard');
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0 }}>{t('nav_yard_pricing')}</h2>
        <button className="btn btn-secondary btn-sm" onClick={async () => {
          const noQr = sharjahUnits.filter(u => !u.qr_code_url && !isSold(u));
          if (noQr.length === 0) {
            // All have QR — print all
            setQrGenerating('preparing');
            const urls = await Promise.all(displayUnits.filter(u => !isSold(u)).map(u => ensureQR(u)));
            const size = window.confirm('Small labels (30×30mm)? Cancel for large tags (40×40mm).') ? 'small' : 'large';
            printQRGrid(displayUnits.filter(u => !isSold(u)), urls, size);
            setQrGenerating('');
          } else {
            // Generate missing first
            setQrGenerating(`Generating: 0 of ${noQr.length}...`);
            for (let i = 0; i < noQr.length; i++) {
              setQrGenerating(`Generating: ${i + 1} of ${noQr.length}...`);
              await ensureQR(noQr[i]);
            }
            setQrGenerating('');
            forceUpdate(v => v + 1);
          }
        }}>🖨 {qrGenerating || 'Print All QR Codes'}</button>
      </div>

      {/* QR Missing Banner */}
      {/* QR warning removed — QR codes are generated on-the-fly from SKU */}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="inv-filter-select" value={containerFilter} onChange={e => setContainerFilter(e.target.value)}>
          <option value="all_sharjah">All Sharjah Units</option>
          <option value="unlinked">Unlinked Units</option>
          {sharjahContainers.map(c => <option key={c.id} value={c.id}>{c.number || c.id} — {c.destination || ''}</option>)}
        </select>
        <select className="inv-filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="new">🆕 New Arrivals</option>
          <option value="has_bids">Has Bids</option>
          <option value="no_bids">No Bids</option>
          <option value="on_hold">⏸ On Hold</option>
        </select>
        <select className="inv-filter-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="most_bids">Most Bids</option>
          <option value="highest">Highest Bid</option>
          <option value="no_bids">No Bids First</option>
          <option value="newest">Recently Added</option>
          <option value="days_in_yard">Days in Yard</option>
        </select>
        <div className="inv-search-wrap">
          <span className="inv-search-icon"><SearchIcon /></span>
          <input className="inv-search" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 160 }} />
        </div>
      </div>

      {/* Summary bar */}
      <div style={{ display: 'flex', gap: 16, padding: '10px 14px', background: 'var(--bg3)', borderRadius: 8, marginBottom: 16, fontSize: 12, color: 'var(--text2)', flexWrap: 'wrap' }}>
        <span>{displayUnits.length} units</span>
        <span>{withBids} with bids</span>
        <span>{noBids} no bids</span>
        {topTotal > 0 && <span style={{ color: 'var(--green)', fontWeight: 700 }}>Top total: ${topTotal.toLocaleString()}</span>}
      </div>

      {/* Unit cards */}
      {displayUnits.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏪</div>
          <div style={{ fontSize: 14 }}>No units in Sharjah yard</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Add units to Sharjah yard from Inventory to start yard pricing</div>
        </div>
      ) : displayUnits.map(unit => {
        const bids = getBids(unit.id);
        const highest = bids.length > 0 ? bids.reduce((max, b) => toUsd(b.amount, b.currency) > toUsd(max.amount, max.currency) ? b : max, bids[0]) : null;
        const sold = isSold(unit);
        const wonBid = allBids.find(b => b.unit_id === unit.id && b.status === 'won');
        const pendingSale = bids.find(b => b.selected_for_sale);

        return (
          <div key={unit.id} className="card" style={{ marginBottom: 10, opacity: sold ? 0.5 : 1, cursor: 'pointer' }} onClick={() => setViewBidsUnit(unit)}>
            <div className="card-body" style={{ padding: '12px 16px' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                {/* Image */}
                <div style={{ width: 72, height: 54, borderRadius: 8, overflow: 'hidden', background: 'var(--bg3)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {unit.images?.length ? <img src={unit.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 20, color: 'var(--text3)' }}>{getUnitIcon(unit)}</span>}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{getUnitName(unit)}</div>
                  <div style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'monospace' }}>{unit.sku}{unit.chassis ? ` · ${unit.chassis}` : ''}</div>
                  {(unit.grade || unit.mileage || unit.color) && (
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                      {unit.grade ? `Grade: ${unit.grade}` : ''}{unit.grade && unit.mileage ? ' · ' : ''}{unit.mileage ? `${Number(unit.mileage).toLocaleString()} km` : ''}{unit.color ? ` · ${unit.color}` : ''}
                    </div>
                  )}

                  {/* Badges row */}
                  <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                    {isNewArrival(unit) && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'rgba(88,166,255,0.15)', color: 'var(--blue)', fontWeight: 700 }}>🆕 NEW</span>}
                    {isOnHold(unit) && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'rgba(210,153,34,0.15)', color: 'var(--yellow)', fontWeight: 700 }}>⏸ HELD for {(DB.get('customers') || []).find(c => c.id === unit.hold_for_customer_id)?.name || '?'}</span>}
                    {unit.hold_requested_by && !isOnHold(unit) && !unit.hold_for_customer_id && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'var(--bg3)', color: 'var(--text3)' }}>⏸ Hold requested</span>}
                    {(Array.isArray(unit.condition_notes) ? unit.condition_notes : []).length > 0 && (
                      <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'var(--bg3)', color: 'var(--text3)', cursor: 'pointer' }}
                        title={(Array.isArray(unit.condition_notes) ? unit.condition_notes : []).map(n => n.note).join('; ')}>
                        📝 {(Array.isArray(unit.condition_notes) ? unit.condition_notes : []).length} note{(Array.isArray(unit.condition_notes) ? unit.condition_notes : []).length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {daysInYard(unit) >= 14 && !isSold(unit) && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'rgba(248,81,73,0.15)', color: 'var(--red)' }}>🔴 {daysInYard(unit)}d</span>}
                    {daysInYard(unit) >= 7 && daysInYard(unit) < 14 && !isSold(unit) && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'rgba(210,153,34,0.15)', color: 'var(--yellow)' }}>⚠️ {daysInYard(unit)}d</span>}
                  </div>

                  {/* Bid info */}
                  {sold && wonBid && (
                    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--green)', fontWeight: 700 }}>
                      ✅ SOLD — {formatCur(wonBid.amount, wonBid.currency)} — {wonBid.customer_name}
                      <div style={{ fontSize: 10, color: wonBid.payment_status === 'paid' ? 'var(--green)' : 'var(--yellow)' }}>
                        Payment: {wonBid.payment_status === 'paid' ? 'Paid ✅' : 'Awaiting payment'}
                      </div>
                    </div>
                  )}

                  {!sold && pendingSale && (
                    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--yellow)' }}>
                      ⏳ Sale pending approval — {formatCur(pendingSale.amount, pendingSale.currency)} to {pendingSale.customer_name}
                    </div>
                  )}

                  {!sold && !pendingSale && highest && (
                    <div style={{ marginTop: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)' }}>
                        🏆 HIGHEST: {formatCur(highest.amount, highest.currency)} — {highest.customer_name}
                        {highest.is_repeat_buyer && ' ✅'}
                        <span style={{ color: 'var(--text3)', fontWeight: 400, marginLeft: 6, fontSize: 10 }}>{timeAgo(highest.created_at)}</span>
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                        📊 {bids.length} bid{bids.length !== 1 ? 's' : ''} total
                        {bids.length > 1 && ` | Avg: ${formatCur(Math.round(bids.reduce((s, b) => s + b.amount, 0) / bids.length), highest.currency)}`}
                      </div>
                    </div>
                  )}

                  {!sold && !pendingSale && !highest && (
                    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--yellow)' }}>⚠️ No bids yet</div>
                  )}
                </div>
              </div>

              {/* Actions */}
              {!sold && (
                <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                  <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }} onClick={() => setAddBidUnit(unit)}>
                    <PlusIcon /> Add Bid
                  </button>
                  {bids.length > 0 && (
                    <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => setViewBidsUnit(unit)}>
                      View Bids ({bids.length})
                    </button>
                  )}
                  {highest && !pendingSale && (
                    <button className="btn btn-success btn-sm" style={{ fontSize: 11 }} onClick={() => setViewBidsUnit(unit)}>
                      Sell to Highest
                    </button>
                  )}
                  <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={async () => {
                    const url = await ensureQR(unit);
                    if (url) setPrintQrUnit({ ...unit, qr_code_url: url });
                  }}>🖨</button>
                  <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => setHoldReqUnit(unit)}>⏸</button>
                  <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => setCondNoteUnit(unit)}>📝</button>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Modals */}
      {addBidUnit && <AddBidModal unit={addBidUnit} user={user} onClose={() => setAddBidUnit(null)} onSaved={() => { setAddBidUnit(null); forceUpdate(v => v + 1); }} />}
      {viewBidsUnit && <ViewBidsModal unit={viewBidsUnit} user={user} onClose={() => setViewBidsUnit(null)} onSaved={() => forceUpdate(v => v + 1)} onGoToCustomer={(custId) => { setViewBidsUnit(null); if (setPage) { localStorage.setItem('bigstar_selected_customer', custId); setPage('customers'); } }} />}

      {/* Print QR Modal */}
      {printQrUnit && (
        <div className="modal-overlay" onClick={() => setPrintQrUnit(null)}>
          <div className="modal" style={{ maxWidth: 360 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Print QR Sticker</h3>
              <button className="modal-close" onClick={() => setPrintQrUnit(null)}>×</button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center', padding: 20 }}>
              {printQrUnit.qr_code_url && <img src={printQrUnit.qr_code_url} alt="QR" style={{ width: 180, height: 180, imageRendering: 'pixelated', borderRadius: 8 }} />}
              <div style={{ fontSize: 11, color: 'var(--cyan)', fontFamily: 'monospace', marginTop: 8 }}>{printQrUnit.sku}</div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{getUnitName(printQrUnit)}</div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
                <button className="btn btn-primary btn-sm" onClick={() => printSingleQR(printQrUnit.qr_code_url, 'small')}>Small 30×30mm</button>
                <button className="btn btn-secondary btn-sm" onClick={() => printSingleQR(printQrUnit.qr_code_url, 'large')}>Large 40×40mm</button>
              </div>
              <a href={printQrUnit.qr_code_url} download={`QR-${printQrUnit.sku}.png`} className="btn btn-secondary btn-sm" style={{ marginTop: 8, display: 'inline-block' }}>📥 Download PNG</a>
            </div>
          </div>
        </div>
      )}

      {/* Condition Note Modal */}
      {condNoteUnit && (() => {
        const NoteModal = () => {
          const [note, setNote] = useState('');
          const [photos, setPhotos] = useState([]);
          const [severity, setSeverity] = useState('');
          const [uploading, setUploading] = useState(false);
          const fileRef = useRef();
          const handleUpload = async (e) => {
            setUploading(true);
            for (const f of e.target.files) { const url = await compressAndUpload(f); if (url) setPhotos(prev => [...prev, url]); }
            setUploading(false); e.target.value = '';
          };
          const handleSave = () => {
            if (!note.trim() || !severity) return;
            const key = condNoteUnit._dbKey || (condNoteUnit.make ? 'vehicles' : condNoteUnit.engineName ? 'engines' : 'spareParts');
            const list = DB.get(key) || [];
            const existing = Array.isArray(condNoteUnit.condition_notes) ? condNoteUnit.condition_notes : [];
            const entry = { id: DB.genId('CN'), note: note.trim(), photos, severity, reported_by: user.id, reported_by_name: user.name, reported_at: new Date().toISOString() };
            DB.set(key, list.map(u => u.id === condNoteUnit.id ? { ...u, condition_notes: [...existing, entry] } : u));
            Notifications.add('general', 'Condition Note', `${user.name} noted on ${getUnitName(condNoteUnit)}: ${note.trim().substring(0, 50)}`, {});
            setCondNoteUnit(null); forceUpdate(v => v + 1);
          };
          return (
            <div className="modal-overlay" onClick={() => setCondNoteUnit(null)}>
              <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
                <div className="modal-header"><h3 className="modal-title">Condition Note</h3><button className="modal-close" onClick={() => setCondNoteUnit(null)}>×</button></div>
                <div className="modal-body" style={{ padding: 16 }}>
                  <div style={{ fontSize: 13, marginBottom: 12 }}>Unit: <strong>{getUnitName(condNoteUnit)}</strong></div>
                  <div className="form-group" style={{ marginBottom: 10 }}>
                    <label className="form-label">What did you notice? <span className="req">*</span></label>
                    <textarea className="form-input" rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Describe the condition..." style={{ resize: 'vertical' }} />
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <label className="form-label">Photos (recommended)</label>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                      {photos.map((url, i) => <img key={i} src={url} alt="" style={{ width: 56, height: 42, borderRadius: 4, objectFit: 'cover' }} />)}
                    </div>
                    <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()}>📸 Take Photos</button>
                    <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple style={{ display: 'none' }} onChange={handleUpload} />
                    {uploading && <div style={{ fontSize: 10, color: 'var(--blue)', marginTop: 4 }}>Uploading...</div>}
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label className="form-label">Severity <span className="req">*</span></label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {[['minor', 'Minor — cosmetic'], ['moderate', 'Moderate — affects value'], ['major', 'Major — significant']].map(([id, label]) => (
                        <button key={id} className={`btn btn-sm ${severity === id ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1, fontSize: 11, padding: '10px 4px' }} onClick={() => setSeverity(id)}>{label}</button>
                      ))}
                    </div>
                  </div>
                  <button className="btn btn-success" style={{ width: '100%' }} disabled={!note.trim() || !severity || uploading} onClick={handleSave}><CheckIcon /> Save Note</button>
                </div>
              </div>
            </div>
          );
        };
        return <NoteModal />;
      })()}

      {/* Hold Request Modal */}
      {holdReqUnit && (() => {
        const HoldModal = () => {
          const [phone, setPhone] = useState('');
          const [custName, setCustName] = useState('');
          const [foundCust, setFoundCust] = useState(null);
          const [duration, setDuration] = useState('2h');
          const [reason, setReason] = useState('');
          useEffect(() => {
            const clean = phone.replace(/[^\d+]/g, '');
            if (clean.length >= 6) {
              const found = findCustomerByPhone(clean);
              setFoundCust(found || null);
              if (found) setCustName(found.name);
            } else setFoundCust(null);
          }, [phone]);
          const handleSubmit = () => {
            if ((!foundCust && !custName.trim()) || phone.replace(/[^\d+]/g, '').length < 6) return;
            const customer = foundCust || createWalkInCustomer(phone, custName);
            const durations = { '1h': 1, '2h': 2, 'tomorrow': 24 };
            const hours = durations[duration] || 2;
            const holdUntil = new Date(Date.now() + hours * 3600000).toISOString();
            if (isAdmin) {
              // Admin places directly
              const key = holdReqUnit._dbKey || (holdReqUnit.make ? 'vehicles' : holdReqUnit.engineName ? 'engines' : 'spareParts');
              const list = DB.get(key) || [];
              DB.set(key, list.map(u => u.id === holdReqUnit.id ? { ...u, hold_for_customer_id: customer.id, hold_until: holdUntil, hold_approved_by: user.id, hold_reason: reason.trim() } : u));
              Notifications.add('general', 'Hold Placed', `${getUnitName(holdReqUnit)} held for ${customer.name} until ${new Date(holdUntil).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`, {});
            } else {
              // Request hold
              const key = holdReqUnit._dbKey || (holdReqUnit.make ? 'vehicles' : holdReqUnit.engineName ? 'engines' : 'spareParts');
              const list = DB.get(key) || [];
              DB.set(key, list.map(u => u.id === holdReqUnit.id ? { ...u, hold_requested_by: user.id, hold_reason: reason.trim(), _hold_request: { customer_id: customer.id, customer_name: customer.name, duration, holdUntil, requested_by: user.name, requested_at: new Date().toISOString() } } : u));
              Notifications.add('general', 'Hold Requested', `${user.name} requests hold: ${getUnitName(holdReqUnit)} for ${customer.name} — ${duration === 'tomorrow' ? 'until tomorrow' : duration}`, {});
            }
            setHoldReqUnit(null); forceUpdate(v => v + 1);
          };
          return (
            <div className="modal-overlay" onClick={() => setHoldReqUnit(null)}>
              <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
                <div className="modal-header"><h3 className="modal-title">{isAdmin ? 'Place Hold' : 'Request Hold'}</h3><button className="modal-close" onClick={() => setHoldReqUnit(null)}>×</button></div>
                <div className="modal-body" style={{ padding: 16 }}>
                  <div style={{ fontSize: 13, marginBottom: 12 }}>Unit: <strong>{getUnitName(holdReqUnit)}</strong></div>
                  <div className="form-group" style={{ marginBottom: 10 }}>
                    <label className="form-label">Customer Phone</label>
                    <input className="form-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+971 55..." style={{ fontFamily: 'monospace' }} />
                    {foundCust && <div style={{ fontSize: 11, color: 'var(--green)', marginTop: 4 }}>✅ {foundCust.name}</div>}
                    {!foundCust && phone.replace(/[^\d+]/g, '').length >= 6 && <input className="form-input" value={custName} onChange={e => setCustName(e.target.value)} placeholder="Customer name" style={{ marginTop: 6 }} />}
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <label className="form-label">Hold Duration</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {[['1h', '1 hour'], ['2h', '2 hours'], ['tomorrow', 'Until tomorrow']].map(([id, label]) => (
                        <button key={id} className={`btn btn-sm ${duration === id ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1 }} onClick={() => setDuration(id)}>{label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom: 14 }}>
                    <label className="form-label">Reason (optional)</label>
                    <input className="form-input" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Customer coming back with cash" />
                  </div>
                  <button className="btn btn-success" style={{ width: '100%' }} disabled={(!foundCust && !custName.trim()) || phone.replace(/[^\d+]/g, '').length < 6} onClick={handleSubmit}>
                    <CheckIcon /> {isAdmin ? 'Place Hold' : 'Request Hold'}
                  </button>
                </div>
              </div>
            </div>
          );
        };
        return <HoldModal />;
      })()}
    </div>
  );
}

export default YardPricing;
